import {
  CompanyEmploymentType,
  DayOfWeek,
  ForwardFillMax,
  ForwardFillRenotificationMethod,
  PaymentType,
  ScheduledBreak,
  ShiftTag,
} from '..'
import { BGCRequirement } from './account-status'
import { BackfillSettingsDto } from './backfill'
import {
  GenderPreference,
  RecordStatus,
  RelatedShiftsType,
  RequiredMultiShiftType,
  TestAccountType,
} from './base'
import { FormatLineItemType, InvoicingTeam } from './invoice'
import { InternalUserRole } from './internal-users'
import { Address, Location, LocationResponse } from './locations'
import { ShiftAttribute } from './shift-attributes'
import { CancellationSettings, ConsistencyType, ShiftTeam } from './shifts'
import { IconName } from './svg-types'
import { WorkerCertification } from './worker-certification'
import { RequiredAttributeLevel, RoleAttributeCategory } from './worker-profile'
import { TierLevel } from './worker-tiers'

export enum BreakType {
  PAID = 'PAID',
  AUTO_UNPAID = 'AUTO_UNPAID',
  MANUAL_UNPAID = 'MANUAL_UNPAID',
  NONE = 'NONE',
}

export enum CompanyCategory {
  WAREHOUSE = 'WAREHOUSE',
  EVENTS = 'EVENTS',
  FOOD_PRODUCTION = 'FOOD_PRODUCTION',
  MANUFACTURING = 'MANUFACTURING',
  BEVERAGE_PRODUCTION = 'BEVERAGE_PRODUCTION',
  COSMETICS_PRODUCTION = 'COSMETICS_PRODUCTION',
  CONTRACT_PACKAGING = 'CONTRACT_PACKAGING',
  CONTRACT_MANUFACTURING = 'CONTRACT_MANUFACTURING',
  THREE_PL = 'THREE_PL',
  FULFILLMENT = 'FULFILLMENT',
  FINAL_MILE = 'FINAL_MILE',
  DISTRIBUTION = 'DISTRIBUTION',
  CROSS_DOCK_DISTRIBUTION = 'CROSS_DOCK_DISTRIBUTION',
  FURNITURE_PRODUCTION_AND_DISTRIBUTION = 'FURNITURE_PRODUCTION_AND_DISTRIBUTION',
  TIRE_PRODUCTION_AND_DISTRIBUTION = 'TIRE_PRODUCTION_AND_DISTRIBUTION',
  PRINTING = 'PRINTING',
  FOOD_PREPARATION_AND_SERVICE = 'FOOD_PREPARATION_AND_SERVICE',
}

export const CompanyCategoryValues: { [key in CompanyCategory]: string } = {
  [CompanyCategory.WAREHOUSE]: 'Warehouse',
  [CompanyCategory.EVENTS]: 'Events',
  [CompanyCategory.FOOD_PRODUCTION]: 'Food Production',
  [CompanyCategory.MANUFACTURING]: 'Manufacturing',
  [CompanyCategory.BEVERAGE_PRODUCTION]: 'Beverage Production',
  [CompanyCategory.COSMETICS_PRODUCTION]: 'Cosmetics Production',
  [CompanyCategory.CONTRACT_PACKAGING]: 'Contract Packaging',
  [CompanyCategory.CONTRACT_MANUFACTURING]: 'Contract Manufacturing',
  [CompanyCategory.THREE_PL]: '3PL',
  [CompanyCategory.FULFILLMENT]: 'Fulfillment',
  [CompanyCategory.FINAL_MILE]: 'Final Mile',
  [CompanyCategory.DISTRIBUTION]: 'Distribution',
  [CompanyCategory.CROSS_DOCK_DISTRIBUTION]: 'Cross Dock Distribution',
  [CompanyCategory.FURNITURE_PRODUCTION_AND_DISTRIBUTION]:
    'Furniture Production and Distribution',
  [CompanyCategory.FOOD_PREPARATION_AND_SERVICE]:
    'Food Preparation and Service',
  [CompanyCategory.TIRE_PRODUCTION_AND_DISTRIBUTION]:
    'Tire Production and Distribution',
  [CompanyCategory.PRINTING]: 'Printing',
}

export const CompanyCategoryIcon: { [key in CompanyCategory]: IconName } = {
  [CompanyCategory.WAREHOUSE]: 'packageLight',
  [CompanyCategory.EVENTS]: 'calendarLight',
  [CompanyCategory.FOOD_PRODUCTION]: 'foodProduction',
  [CompanyCategory.MANUFACTURING]: 'manufacturing',
  [CompanyCategory.BEVERAGE_PRODUCTION]: 'drinkLight',
  [CompanyCategory.COSMETICS_PRODUCTION]: 'lipstick',
  [CompanyCategory.CONTRACT_PACKAGING]: 'packageLight',
  [CompanyCategory.CONTRACT_MANUFACTURING]: 'formLight',
  [CompanyCategory.THREE_PL]: 'threePL',
  [CompanyCategory.FULFILLMENT]: 'delivery',
  [CompanyCategory.FINAL_MILE]: 'pinLight',
  [CompanyCategory.DISTRIBUTION]: 'distributionCenters',
  [CompanyCategory.CROSS_DOCK_DISTRIBUTION]: 'crossDock',
  [CompanyCategory.FURNITURE_PRODUCTION_AND_DISTRIBUTION]: 'cabinetLight',
  [CompanyCategory.TIRE_PRODUCTION_AND_DISTRIBUTION]: 'tire',
  [CompanyCategory.PRINTING]: 'formLight',
  [CompanyCategory.FOOD_PREPARATION_AND_SERVICE]: 'foodProduction',
}

export const CompanyCategoryNameToId = new Map<string, CompanyCategory>(
  Object.values(CompanyCategory).map((category) => [
    CompanyCategoryValues[category],
    category,
  ]),
)

export type CompanyGroup = {
  id: string
  name: string
  companyIds: string[]
  companies?: Array<{
    id: string
    employerName: string
  }>
}

export type Company = {
  companyId: string
  employerName: string
  createdAt: Date
  address: Address
  coords?: Location
  shortLocation?: string
  category?: CompanyCategory
  activeEmploymentTypes?: CompanyEmploymentType[]
  referralCode?: string
  companyDescription?: string
  companyPhoneNumber?: string
  rating?: number
  baseMarkup?: number
  calculatedMarkup?: number
  bonusTimeCalculatedMarkup?: number | null
  workerMedia?: WorkerMedia[]
  bannerUrl?: string
  companyLogo?: string
  minHourlyPayRate?: number
  breakType?: BreakType
  isApproved: boolean
  agreements?: {
    acceptedCustomerAgreement?: Date
    acceptedFeesAndTerms?: Date
  }
  isHandpicked: boolean
  salesforceId?: string
  googleDriveFolderId?: string
  testAccountType?: TestAccountType
  backfillSettings?: BackfillSettingsDto
  minimumAcceptedTier?: TierLevel
  reliabilityThreshold?: number // Shift acceptance override
  unprovenWorkerThreshold?: number // Shift acceptance override
  hasSameDayClockInAndOutCodes?: boolean
  hasPreShiftConfirmationRobocall?: boolean
  extraBGCRequirement: BGCRequirement
  showAddressToNonWorkersForCompany: boolean
  showNameToNonWorkersForCompany: boolean
  allowPostingPayByUnit?: boolean
  shiftTags?: ShiftTag[]
  billingEmails?: string[]
  allowsDynamicOverbooking?: boolean
  enabledCertifications?: string[]
  allowGenderPreference?: boolean
  showRequiredMultiShiftToggle?: boolean
  showSearch?: boolean
  requiredMultiShiftType?: RequiredMultiShiftType
  kioskPin?: string
  disableLeftFencePromptOverride: boolean
  requireW9Authorization: boolean
  defaultBreaks?: ScheduledBreak | null
  autoDeductManualBreaks?: boolean
  paymentType?: PaymentType
  paymentDay?: string
  paymentTimeSlot?: string
  paymentDayOccurrence?: string
  autoWeeklyPaymentScheduleId?: string
  minutesToClockInEarlyBy?: number | null
  upflowEnabled?: boolean
  minutesAheadForShiftPosting?: number
  timesheetEmail?: string
  shiftReasons?: Record<string, string[]>
  companyGroupId?: string
  timeRoundingIncrement?: number
  snapEarlyClockInToStartTime?: boolean
  snapLateClockInToStartTime?: boolean
  snapEarlyClockOutToEndTime?: boolean
  snapLateClockOutToEndTime?: boolean
  consistencyType?: ConsistencyType
  team?: ShiftTeam
  cancellationSettings?: CancellationSettings
  minimumPaidTime?: number
  minimumFlaggedTime?: number
  isExemptFromPalaceOfJustice?: boolean
  regionAssignedOperators?: CompanyRegionAssignedOperatorsEntry[]
  requireInvoiceGroupOnShifts?: boolean
  requireExternalLocationId?: boolean
}

export type AssignedOperatorForCompany = {
  roleType: string
  internalUser: {
    id: string
    firstName: string
    lastName: string
    email: string
    internalUserRole?: InternalUserRole
  } | null
  isCompanyOverride: boolean
}

export type CompanyRegionAssignedOperatorsEntry = {
  regionId: string
  regionName?: string
  assignedOperators: AssignedOperatorForCompany[]
}

export type UpdateCompanyBody = Omit<
  Partial<Company>,
  'timeRoundingIncrement'
> & {
  timeRoundingIncrement?: number | null
}

// Potentially add "requirements" to include company specific reqs like drug testing.
export type CompanyResponse = {
  id: string
  createdAt: Date
  updatedAt: Date
  salesforceId: string | null
  isApproved: boolean
  employerName: string
  category: CompanyCategory | null
  calculatedMarkup: number | null
  baseMarkup: number | null
  companyLogo: string | null
  bannerUrl: string
  rating: number
  paymentType: PaymentType | null
  referralCode: string
  breakType: BreakType | null
  acceptedCustomerAgreement: Date | null
  acceptedFeesAndTerms: Date | null
  allowsBackfill: false
  lateTolerance: number
  backfillLocationId: string | null
  backfillLocationStartOffset: number | null
  enabledCertifications: string[]
  reliabilityThreshold: number | null
  minimumAcceptedTier: TierLevel
  unprovenWorkerThreshold: number | null
  allowPostingPayByUnit: boolean | null
  extraBGCRequirement: BGCRequirement
  forwardFillMax: ForwardFillMax | null
  forwardFillRenotificationMethod: ForwardFillRenotificationMethod | null
  workerReportCycleCadence: string | null
  workerReportDestination: string | null
  workerReportEmails: string[]
  relatedShiftsType: RelatedShiftsType | null
  requiredMultiShiftType: RequiredMultiShiftType | null
  addresses: Address[]
  showRequiredMultiShiftToggle: boolean | null
  requireExternalLocationId?: boolean
  activeEmploymentTypes?: CompanyEmploymentType[]
  locations?: LocationResponse[]
  regions?: CompanyRegionAssignedOperatorsEntry[]
}

export type CompanyAddress = {
  city: string
  state: string
  street1: string
  street2?: string
  postalCode: string
}

export type PaginatedCompanyItem = {
  id: string
  employerName: string
  regions?: CompanyRegionAssignedOperatorsEntry[]
  category?: string | null
  addresses?: CompanyAddress[]
  isApproved?: boolean
  activeEmploymentTypes?: CompanyEmploymentType[]
  createdAt?: Date
  updatedAt?: Date
}

export type PaginatedCompanyResponse = {
  items: PaginatedCompanyItem[]
  page: number
  pageSize: number
  pageCount: number
  totalCount: number
}

export type Companies = {
  [key: number]: Company
}

export type ShiftCompanyResponse = {
  company: {
    companyId: string
    address: Address
    bannerUrl?: string
    createdAt: Date
    employerName: string
    companyLogo?: string
    rating?: number
    workerMedia?: WorkerMedia[]
  }
}

export enum RoleFamily {
  WAREHOUSE_PICKER = 'WAREHOUSE_PICKER',
  WAREHOUSE_PACKER = 'WAREHOUSE_PACKER',
  GRADER = 'GRADER',
  STANDUP_FORKLIFT_OPERATOR = 'STANDUP_FORKLIFT_OPERATOR',
  CHERRY_PICKER_OPERATOR = 'CHERRY_PICKER_OPERATOR',
  SANITATION_WORKER = 'SANITATION_WORKER',
  PRODUCTION_ASSOCIATE = 'PRODUCTION_ASSOCIATE',
  WAREHOUSE_LUMPER = 'WAREHOUSE_LUMPER',
  PRINTER_OPERATOR = 'PRINTER_OPERATOR',
  FORKLIFT_OPERATOR = 'FORKLIFT_OPERATOR',
  FOOD_PRODUCTION_ASSOCIATE = 'FOOD_PRODUCTION_ASSOCIATE',
  MAINTENANCE_TECH = 'MAINTENANCE_TECH',
  EXTRUSION_MOLDING_OPERATOR = 'EXTRUSION_MOLDING_OPERATOR',
  STAGEHAND = 'STAGEHAND',
  EVENT_STAFF = 'EVENT_STAFF',
  PRODUCTION_MACHINE_OPERATOR = 'PRODUCTION_MACHINE_OPERATOR',
  CNC_OPERATOR = 'CNC_OPERATOR',
  REACH_TRUCK_OPERATOR = 'REACH_TRUCK_OPERATOR',
  LICENSED_SECURITY_GUARD = 'LICENSED_SECURITY_GUARD',
  ONSITE_LEAD = 'ONSITE_LEAD',
  WELDER_FABRICATOR = 'WELDER_FABRICATOR',
  RESTORATION_TECHNICIAN = 'RESTORATION_TECHNICIAN',
  CLERICAL_ADMIN = 'CLERICAL_ADMIN',
  SHIPPING_RECEIVING = 'SHIPPING_RECEIVING',
  OTHER = 'OTHER',
}

export type Role = {
  roleId: string
  activeRoleId?: string
  companyId: string
  roleName: string
  roleDescription: string
  defaultPayRate: number
  requiredAttire: string
  requiredCertifications?: WorkerCertification['certificationType'][]
  requiredAttributes?: ShiftAttribute[]
  requiredAttributeLevels?: RequiredAttributeLevel[]
  hasPreShiftConfirmationRobocall?: boolean
  extraBGCRequirement: BGCRequirement
  freeformAttributes?: Partial<Record<RoleAttributeCategory, string>>
  videoIds?: string[]
  resumeUploadRequired?: boolean
  requiredTrainingIds?: string[]
  genderPreference?: GenderPreference
  minimumAgeRequirement?: number
  recordStatus?: RecordStatus
  location?: LocationResponse
  roleFamily?: RoleFamily
  parentInvoiceGroupId?: string | null
}

type PaymentSettings = {
  stripeCustomerId?: string
  email: string
  phoneNumber: string
}

export enum InvoiceApproval {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL', // set by ops
  MANUAL_NEW = 'MANUAL_NEW', // by default, new businesses will need their first 3 invoices manually approved
}

export enum InvoiceFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
}

export enum InvoiceShiftRangeInclusionMethod {
  START_TIME = 'START_TIME',
  END_TIME = 'END_TIME',
}

export enum CreditNoteStatementMode {
  SEPARATED_CREDIT_NOTES = 'SEPARATED_CREDIT_NOTES',
  COMBINED_CREDIT_NOTES = 'COMBINED_CREDIT_NOTES',
}

export type Billing = {
  billingId: string
  companyId: string
  paymentSettings: PaymentSettings
  automaticChargesEnabled: boolean
  drugTestPassThroughEnabled: boolean
  invoiceApproval: InvoiceApproval
  invoiceLineItemType: FormatLineItemType
  invoiceFrequency: InvoiceFrequency
  workWeekEnd: DayOfWeek
  invoiceDaysUntilDue: number
  contactName?: string
  invoiceCompanyName?: string
  creditNoteStatementMode: CreditNoteStatementMode
  invoiceShiftRangeInclusionMethod: InvoiceShiftRangeInclusionMethod
  invoicingTeam: InvoicingTeam
  biweeklyAnchorDate?: string | null
}

export type ACHPaymentDetails = {
  bankName: string
  routingNumber: string
  accountNumber: string
  swiftCode: string
}

export const DEFAULT_BASE_MARKUP = 0.4

export interface BillingUpdate extends Omit<
  Partial<Billing>,
  'paymentSettings'
> {
  paymentSettings?: Partial<PaymentSettings>
}

export type WorkerYouTubeVideo = {
  type: WorkerMediaType.YOUTUBE
  videoId: string
}

export type WorkerVideo = {
  type: WorkerMediaType.VIDEO
  videoUrl: string
  thumbnailUrl?: string
}

export type WorkerImage = {
  type: WorkerMediaType.IMAGE
  imageUrl: string
}

export enum WorkerMediaType {
  VIDEO = 'Video',
  IMAGE = 'Image',
  YOUTUBE = 'YouTubeVideo',
}

export type WorkerMedia = { order: number } & (
  | WorkerImage
  | WorkerVideo
  | WorkerYouTubeVideo
)
